import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Link, useRouteMatch } from "react-router-dom";
import { useSelector } from "react-redux";
import Button from "../Shared/Button";
import {
  getAllSMSTemplates,
  updateSMSTemplate,
  createAutoSmsTemplateSet,
  renameAutoSmsTemplateSet,
} from "../../services/smsTemplates";
import { smsTemplatesSorter, isPermit } from "../../utils";
import { SCOPE } from "../../constants";
import PlaceholderHelpLink from "./PlaceholderHelpLink";

const SET_NAME_MODAL = {
  CREATE: "create",
  RENAME: "rename",
};

const SMSTemplatesPanel = () => {
  const { url } = useRouteMatch();
  const currentUser = useSelector((state) => state.user?.currentUser);
  const canManageTemplateSets = isPermit(currentUser?.role, SCOPE.OTHER_SETTINGS);

  const [templateSets, setTemplateSets] = useState([]);
  const [templateSetId, setTemplateSetId] = useState("");
  const [templateUpdated, setTemplateUpdated] = useState(false);
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false);
  const [notification, setNotification] = useState(null);

  const [setNameModal, setSetNameModal] = useState(null);
  const [setNameInput, setSetNameInput] = useState("");
  const [setNameModalError, setSetNameModalError] = useState("");
  const [isSavingSetMeta, setIsSavingSetMeta] = useState(false);

  const clearNotification = () => {
    if (!_.isNull(notification)) {
      setNotification(null);
    }
  };

  const closeSetNameModal = () => {
    if (isSavingSetMeta) {
      return;
    }
    setSetNameModal(null);
    setSetNameInput("");
    setSetNameModalError("");
  };

  const openCreateModal = () => {
    clearNotification();
    setSetNameInput("");
    setSetNameModalError("");
    setSetNameModal(SET_NAME_MODAL.CREATE);
  };

  const openRenameModal = (name) => {
    clearNotification();
    setSetNameInput(name);
    setSetNameModalError("");
    setSetNameModal(SET_NAME_MODAL.RENAME);
  };

  const handleSubmit = async () => {
    clearNotification();
    setIsUpdatingTemplate(true);
    const seletectTemplateSet = templateSets.filter((set) => set.id === templateSetId)[0];
    if (!_.isNil(seletectTemplateSet)) {
      const result = await updateSMSTemplate(seletectTemplateSet);
      if (result.error) {
        setNotification({
          success: false,
          message: "Something went wrong, please try again later!",
        });
      } else {
        setNotification({
          success: true,
          message: "Update templates successfully!",
        });
        setTemplateUpdated(false);
      }
    }
    setIsUpdatingTemplate(false);
  };

  const handleUpdateTemplate = (templateSetId, templateId, content) => {
    clearNotification();
    setTemplateUpdated(true);
    setTemplateSets(
      templateSets.map((set) => {
        if (set.id !== templateSetId) {
          return set;
        }
        set.templates = (set.templates || []).map((template) => {
          if (template.id !== templateId) {
            return template;
          }
          template.content = content;
          return template;
        });
        return set;
      })
    );
  };

  const loadTemplateSets = async () => {
    const response = await getAllSMSTemplates({ type: "appointment_auto_sms" });
    setTemplateSets(response.result);
    return response.result;
  };

  useEffect(() => {
    loadTemplateSets();
  }, []);

  const handleSelectTemplateSet = (id) => {
    setTemplateSetId(id);
  };

  const handleCreateSet = async () => {
    const trimmedName = setNameInput.trim();
    if (!trimmedName) {
      setSetNameModalError("Template set name is required.");
      return;
    }
    setSetNameModalError("");
    setIsSavingSetMeta(true);
    const { result, error } = await createAutoSmsTemplateSet(trimmedName);
    if (error || _.isNil(result)) {
      setSetNameModalError("Failed to create template set, please try again later!");
    } else {
      setTemplateSets((prev) => [...prev, result]);
      setTemplateSetId(result.id);
      closeSetNameModal();
      setNotification({
        success: true,
        message: "Template set created successfully!",
      });
    }
    setIsSavingSetMeta(false);
  };

  const handleRenameSet = async () => {
    const trimmedName = setNameInput.trim();
    if (!templateSetId || !trimmedName) {
      setSetNameModalError("Template set name is required.");
      return;
    }
    setSetNameModalError("");
    setIsSavingSetMeta(true);
    const { error } = await renameAutoSmsTemplateSet({ id: templateSetId, name: trimmedName });
    if (error) {
      setSetNameModalError("Failed to rename template set, please try again later!");
    } else {
      setTemplateSets((prev) => prev.map((set) => (set.id === templateSetId ? { ...set, name: trimmedName } : set)));
      closeSetNameModal();
      setNotification({
        success: true,
        message: "Template set renamed successfully!",
      });
    }
    setIsSavingSetMeta(false);
  };

  const handleSubmitSetName = () => {
    if (setNameModal === SET_NAME_MODAL.CREATE) {
      handleCreateSet();
      return;
    }
    if (setNameModal === SET_NAME_MODAL.RENAME) {
      handleRenameSet();
    }
  };

  const selectedSet = templateSets.filter((set) => set.id === templateSetId)[0];
  const selectedTemplates = _.get(selectedSet, "templates", []);
  const isSetNameModalOpen = !_.isNull(setNameModal);

  return (
    <>
      <div className="columns">
        <div className="column is-one-fifth">
          <aside className="menu">
            <div className="is-flex is-align-items-center is-justify-content-space-between mb-2">
              <p className="menu-label mb-0">Template Sets</p>
              {canManageTemplateSets && (
                <button
                  type="button"
                  className="button is-small is-link is-light is-radiusless"
                  title="Add template set"
                  onClick={openCreateModal}
                >
                  <span className="icon is-small">
                    <i className="fas fa-plus" />
                  </span>
                </button>
              )}
            </div>
            <ul className="menu-list">
              {templateSets.sort(smsTemplatesSorter).map((templateSet) => (
                <li key={templateSet.id} className="is-size-7">
                  <div className="is-flex is-align-items-center">
                    <Link
                      to={url}
                      className={
                        templateSetId === templateSet.id ? "is-active is-radiusless is-flex-grow-1" : "is-flex-grow-1"
                      }
                      onClick={() => handleSelectTemplateSet(templateSet.id)}
                    >
                      {templateSet.name}
                    </Link>
                    {canManageTemplateSets && templateSetId === templateSet.id && (
                      <button
                        type="button"
                        className="button is-small is-white is-radiusless ml-1"
                        title="Rename template set"
                        onClick={() => openRenameModal(templateSet.name)}
                      >
                        <span className="icon is-small">
                          <i className="fas fa-pen" />
                        </span>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </aside>
        </div>
        <div className="column is-half" key={_.get(selectedSet, "id")}>
          <div className="mb-3">
            <PlaceholderHelpLink anchor="auto-sms" />
          </div>
          <p className="menu-label">At GP SMS Templates</p>
          {selectedTemplates
            .filter((template) => template.type === "AT_GP")
            .filter((template) => template.order !== 3)
            .sort((template1, template2) => (template1.order < template2.order ? -1 : 1))
            .map((template) => (
              <article className="message is-link is-small" key={template.id}>
                <div className="message-header">
                  <p>{template.name}</p>
                </div>
                <textarea
                  rows={6}
                  className="textarea is-radiusless is-size-7"
                  onChange={(e) => handleUpdateTemplate(templateSetId, template.id, e.target.value)}
                  defaultValue={template.content}
                />
              </article>
            ))}
          <p className="menu-label">At Home SMS Templates</p>
          {selectedTemplates
            .filter((template) => template.type === "AT_HOME")
            .filter((template) => template.order !== 3)
            .sort((template1, template2) => (template1.order < template2.order ? -1 : 1))
            .map((template) => (
              <article className="message is-link is-small" key={template.id}>
                <div className="message-header">
                  <p>{template.name}</p>
                </div>
                <textarea
                  rows={6}
                  className="textarea is-radiusless is-size-7"
                  onChange={(e) => handleUpdateTemplate(templateSetId, template.id, e.target.value)}
                  defaultValue={template.content}
                />
              </article>
            ))}
          {selectedTemplates.length > 0 ? (
            <Button
              className=""
              text="SUBMIT"
              onClick={handleSubmit}
              isLoading={isUpdatingTemplate}
              disabled={!templateUpdated}
            />
          ) : (
            <></>
          )}
          {!_.isNull(notification) && (
            <div
              className={`notification is-light is-size-7 is-radiusless ${
                notification.success ? "is-success" : "is-danger"
              }`}
            >
              {notification.message}
            </div>
          )}
        </div>
      </div>

      {canManageTemplateSets && (
        <div className={`${isSetNameModalOpen ? "is-active " : ""}modal`}>
          <div className="modal-background" onClick={closeSetNameModal} />
          <div className="modal-content">
            <div className="box is-radiusless is-size-7">
              <p className="title is-6 mb-4">
                {setNameModal === SET_NAME_MODAL.CREATE ? "Create template set" : "Rename template set"}
              </p>
              <div className="field">
                <label className="label is-size-7">Name</label>
                <div className="control">
                  <input
                    className="input is-radiusless is-size-7"
                    placeholder="Template set name"
                    value={setNameInput}
                    disabled={isSavingSetMeta}
                    onChange={(e) => {
                      setSetNameInput(e.target.value);
                      if (setNameModalError) {
                        setSetNameModalError("");
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSubmitSetName();
                      }
                    }}
                  />
                </div>
              </div>
              {!_.isEmpty(setNameModalError) && (
                <article className="message is-danger is-small">
                  <div className="message-body is-radiusless">{setNameModalError}</div>
                </article>
              )}
              <div className="buttons mt-4 mb-0">
                <button
                  type="button"
                  className={`button is-link is-radiusless is-small ${isSavingSetMeta ? "is-loading" : ""}`}
                  disabled={isSavingSetMeta}
                  onClick={handleSubmitSetName}
                >
                  {setNameModal === SET_NAME_MODAL.CREATE ? "Create" : "Save"}
                </button>
                <button
                  type="button"
                  className="button is-light is-radiusless is-small"
                  disabled={isSavingSetMeta}
                  onClick={closeSetNameModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="modal-close is-large"
            aria-label="close"
            disabled={isSavingSetMeta}
            onClick={closeSetNameModal}
          />
        </div>
      )}
    </>
  );
};

export default SMSTemplatesPanel;
